import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { filter, first, map } from 'rxjs/operators';
import { AuthSessionStore } from '../+state';
import { navigateOrReport } from '../util/navigate-or-report.util';

/**
 * Route guard ensuring that the user is authenticated before activating a route.
 *
 * It waits for {@link AuthSessionStore.authenticationInitialized} to be true, then checks
 * {@link AuthSessionStore.isAuthenticated}. If unauthenticated, it navigates to the 'login' route.
 *
 * @returns An {@link import('rxjs').Observable} that resolves to `true` to allow access or `false` to block it.
 * @example
 * import { Routes } from '@angular/router';
 * import { authGuard } from '@indutrust/auth-data';
 *
 * export const routes: Routes = [
 *   {
 *     path: 'dashboard',
 *     canActivate: [authGuard],
 *     loadChildren: async () =>
 *       import('@indutrust/dashboard-feature').then((m) => m.libDashboardRoutes),
 *   },
 * ];
 */
export const authGuard: CanActivateFn = () => {
  const authStore = inject(AuthSessionStore);
  const router = inject(Router);

  // Wait for authenticationInitialized before checking authentication
  return toObservable(authStore.authenticationInitialized).pipe(
    filter((initialized): boolean => initialized),
    first(),
    map(() => {
      if (!authStore.isAuthenticated()) {
        navigateOrReport(router, ['/login'], 'Navigation to login failed');
        return false;
      }
      return true;
    })
  );
};
